import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router';
import { JL } from 'jsnlog';
import ScreenSidebar from 'components/sidebar/ScreenSidebar';
import Sidebar from '../sidebar/Sidebar';
import ExperimentSidebar from '../sidebar/ExperimentSidebar';
import Logo from '../../Icons/RMlogo.png';
import RMErrorModal from 'components/main/Error/RMErrorModal';
import { setProductEdition } from 'redux/reducers/experimentReducer';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import LicenseService from 'business/services/LicenseService';
import RMModal from 'components/modal/RMModal';
import {
  useGlobalData,
  useSetShowLicenseModal,
  useShowLicenseModal,
} from 'redux/hooks/common';
import { IStyle } from 'Styles';
import AutoUpdate from 'components/dashboard/AutoUpdate';
import StackTrace from 'stacktrace-js';
import { Sidebar as SNFSidebar } from 'components/searchAndFilter';
import { useFetchGlobalData } from 'CustomHooks';
import FragmentSidebar from 'components/sidebar/FragmentSidebar';
import {
  clearUserDataAndLogout,
  ErrorMessages,
  getCurrentUser,
  GetIngredients,
  showAlertSnackBar,
  validateVersion,
} from 'utils/Global';
import { useIsGlobalDataLoaded } from 'redux/hooks/experiment';
import init from '../../wasm/tuning/image_tuner';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { getAppVersion } from '../dashboard/AutoUpdate/AutoUpdate.BL';
import { CacheHelper } from 'utils/CacheHelper';
import { selectedSystemTasksState } from 'redux/selectors/StateSelectors';
import NetworkError from 'utils/NetworkError';

// Extend the Window interface
declare global {
  interface Window {
    JL: typeof JL;
  }
}
//This statement stops JL to automatically log backend errors thrice
window.onerror = function (errorMsg, url, lineNumber, column, errorObj) {
  return false;
};

//This statement stops JL to automatically log backend unhandledexceptions thrice
window.onunhandledrejection = function (event) {
  return false;
};

let lastErrorMessage = '';
let lastErrorTime = 0;

const isDuplicate = (msg) => {
  const now = Date.now();
  if (msg === lastErrorMessage && now - lastErrorTime < 200) {
    return true;
  }
  lastErrorMessage = msg;
  lastErrorTime = now;
  return false;
};

const ErrorMessageKeywords = {
  WebsocketHandshake: 'handshake',
  UnexpectedError: 'Unexpected error',
  FailedToFetch: 'Failed to fetch',
};

const showErrorAlert = (message: ErrorMessages) => {
  showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, message);
};

const Layout = (props) => {
  const [token, setToken] = useState<string | null>(null);
  useEffect(() => {
    CacheHelper.get('token').then((value) => setToken(value ?? null));
    const added = CacheHelper.oncacheadded('token', (value) => setToken(value));
    const removed = CacheHelper.oncacheremoved('token', (value) => setToken(value));

    return () => {
      added?.dispose();
      removed?.dispose();
    };
  }, []);
  useFetchGlobalData(token);
  const systemTasks = useRMSelector(selectedSystemTasksState);
  const hasBatchloadUploadInProgress = systemTasks?.some(
    (task) =>
      task.title?.toLocaleLowerCase().includes('creating batchload experiments') &&
      (task.progress == null || task.progress < 100),
  );

  useEffect(() => {
    if (!hasBatchloadUploadInProgress) {
      return;
    }
    const beforeUnloadListener = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', beforeUnloadListener);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadListener);
    };
  }, [hasBatchloadUploadInProgress]);

  const location = useLocation();
  const history = useHistory();
  const path = location.pathname;
  const isFragmentRoute = path.includes('/fragment');
  const expRoute = path.includes('/exp');
  const screenRoute = path.includes('/screen');
  const isLoginRoute =
    path.includes('/login') || path.includes('/forgot') || path.includes('/inputOTP');
  const isErrorRoute =
    path.includes('/accesscontrolerror') || path.includes('/versionmismatch');
  const isPFRoute = path.includes('/protein');
  const isProject = path.includes('/project');
  const isSearchAndFilterRoute = path.includes('/snf');
  const isFeatureSelectionRoute = path.includes('/featureselection');
  const isLicenseViolationRoute = path.includes('licenseviolate');

  const splitPath = path.split('/');
  const globalData = useGlobalData();
  const globalDataLoaded = useIsGlobalDataLoaded();

  const [uncaughtError, setUncaughtError] = useState({
    hasError: false,
    errorMessage: '',
    errorStack: '',
    errorTime: new Date(),
    isLoadingStackTrace: false,
  });
  const dispatch = useRMDispatch();
  const _setProductEdition = useCallback(
    (productEdition: string) => dispatch(setProductEdition(productEdition)),
    [dispatch],
  );
  const showLicensModal = useShowLicenseModal();
  const _setShowLicensModal = useSetShowLicenseModal();

  useEffect(() => {
    const licenseService = new LicenseService();
    licenseService.getProductEdition().then((res) => _setProductEdition(res.edition));

    const loadWasm = async () => {
      try {
        await init();
      } catch (error) {
        console.error('WASM initialization error:', error);
      }
    };

    loadWasm();
  }, []);

  useEffect(() => {
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('error', onError);

    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener('error', onError);
    };
  }, []);

  function onUnhandledRejection(err: PromiseRejectionEvent) {
    const { reason } = err;
    console.error('unhandled rejection occured', { err });
    if (isDuplicate(reason?.message)) {
      return;
    }
    if (err.reason instanceof NetworkError) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Network error. Please check your connection.',
      );
      return;
    }
    if (err.reason?.Transient) {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, reason?.ErrorMessage);
      return;
    }
    /* This is not a proper solution, just a workaround solution for resease, we need to handle these errors in errorboundary */
    switch (reason?.status) {
      case 401:
        clearUserDataAndLogout();
        break;
      case 446:
        history.push({
          pathname: '/registercomputer',
          state: { detail: reason.status },
        });
        break;
      case 445:
        history.push({
          pathname: '/sessiontermination',
          state: { detail: reason.status },
        });
        break;
      case 403:
      case 440:
      case 441:
      case 442:
      case 443:
      case 448:
      case 449:
      case 450:
      case 453: //license updating (auto)
      case 454: //license notification
        history.push({
          pathname: '/licenseviolate',
          state: { detail: err.reason.status },
        });
        break;
      case 444:
        history.push('/seatlicenseviolate');
        break;
      case 447:
      case 451:
        history.push({
          pathname: '/rmmessage',
          state: { detail: err.reason.status },
        });
        break;
      case 452:
        if (history.location.pathname !== '/featureselection') {
          history.push({
            pathname: '/hybridselection',
            state: { detail: err.reason.status },
          });
        }
        break;
      case 400:
        showErrorAlert(ErrorMessages.BAD_REQUEST);
        break;
      case 504:
        showErrorAlert(ErrorMessages.TIMEOUT);
        break;
      default:
        if (reason) {
          const errorString = `${
            reason.ErrorMessage || reason.statusText || reason.message
          }`;
          if (errorString.includes(ErrorMessageKeywords.WebsocketHandshake)) {
            // suppress handshake errors for now(need to replace old implementation), as retry is already in-place
            console.error('WebSocket handshake error:', errorString);
            return;
          }
        }
        if (reason?.StatusCode == 500) {
          const errorString = `${
            reason.ErrorMessage || reason.statusText || reason.message
          }`;
          setUncaughtError({
            hasError: true,
            errorMessage: 'Error: ' + errorString || 'Error',
            errorStack: reason?.StackTrace || reason?.stack,
            errorTime: new Date(),
            isLoadingStackTrace: false,
          });
        } else {
          const errMsg =
            typeof reason === 'string'
              ? ErrorMessageKeywords.UnexpectedError // the detail will be in stack
              : `${reason.ErrorMessage || reason.statusText || reason.message}`;

          const newError = new Error(errMsg);

          newError.stack =
            reason?.StackTrace || reason?.stack
              ? reason?.StackTrace || reason?.stack
              : `${
                  ErrorMessageKeywords.UnexpectedError
                }.\nError: ${errMsg}\nRaw data:\n${getUnexpectedErrorRawData(err)}`;

          throw newError;
        }
    }
  }

  function getUnexpectedErrorRawData(err: PromiseRejectionEvent) {
    if (typeof err.reason === 'string') {
      return err.reason;
    }
    if (typeof err.reason === 'object' && Object.keys(err.reason).length > 0) {
      return JSON.stringify(err.reason);
    }
    return JSON.stringify(err);
  }

  function onError(e: ErrorEvent) {
    const { message, error } = e;

    if (message.includes(ErrorMessageKeywords.WebsocketHandshake)) {
      // suppress handshake errors for now(need to replace old implementation), as retry is already in-place
      console.error('WebSocket handshake error:', message);
      return;
    }
    if (isDuplicate(message)) {
      return;
    }
    if (error.reason?.Transient) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        error.reason?.ErrorMessage,
      );
      return;
    }

    setUncaughtError({
      hasError: true,
      errorMessage: message,
      errorStack: error.stack,
      errorTime: new Date(),
      isLoadingStackTrace: true,
    });

    if (error.stack?.startsWith(ErrorMessageKeywords.UnexpectedError)) {
      setUncaughtError((prvState) => ({
        ...prvState,
        isLoadingStackTrace: false,
        errorStack: error.stack,
      }));
      logJLException(error.stack, message, error);
      return false;
    }

    StackTrace.fromError(error)
      .then((stackErrorList) => {
        console.error({ stackErrorList });
        setUncaughtError((prvState) => ({
          ...prvState,
          isLoadingStackTrace: false,
          errorStack: JSON.stringify(stackErrorList, null, 2),
        }));
        logJLException(stackErrorList, message, error);
      })
      .catch((err) => {
        console.error('Failed to load stack trace', { err });
      })
      .finally(() => {
        // Stop loading if loading still true
        // The stack trace has already been captured earlier, so no need to add it again.
        setUncaughtError((prvState) => ({
          ...prvState,
          isLoadingStackTrace: false,
        }));
      });
    return false;
  }

  const logJLException = (stackErrorList, message, error) => {
    const user = getCurrentUser();
    JL().fatalException(
      {
        msg: 'Uncaught Exception',
        userName: user.name,
        userID: user.id,
        message: message,
        errorStack: JSON.stringify(stackErrorList, null, 2),
      },
      error,
    );
  };

  useEffect(() => {
    async function getVersion() {
      let version = sessionStorage.getItem('version');
      if (!validateVersion(version)) {
        version = await getAppVersion();
        if (validateVersion(version)) {
          sessionStorage.setItem('version', version);
        }
      }
      if (validateVersion(version)) {
        document.title = `ROCK MAKER® v${version}`;
      } else {
        document.title = `ROCK MAKER®`;
      }
    }
    if (!expRoute) {
      getVersion();
    }

    // JL().info('starting client app');
  }, [path]);

  const closeErrorModal = () => {
    setUncaughtError({ ...uncaughtError, hasError: false });
  };

  if (
    !GetIngredients() ||
    !globalDataLoaded ||
    globalData.data === null ||
    globalData.isFetching
  ) {
    return null;
  }

  if (!isLoginRoute && !isPFRoute && !isErrorRoute && !isFeatureSelectionRoute) {
    return (
      <div
        id="layout-wrapper"
        data-testid="layout-wrapper"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: '100%',
          display: 'grid',
          gridTemplateAreas: `'top-left center ' 
                                'left center '`,
          gridTemplateColumns: '4.7%  95.3%',
          gridTemplateRows: '9.6% 90.4%',
          overflow: 'hidden',
        }}
      >
        <div
          id="layout-left-container"
          data-testid="layout-left-container"
          style={{
            gridArea: 'left',
            overflow: 'auto',
            scrollbarWidth: 'none',
          }}
        >
          {!expRoute &&
            !screenRoute &&
            !isLoginRoute &&
            !isErrorRoute &&
            !isSearchAndFilterRoute &&
            !isFragmentRoute && <Sidebar isLicenseViolated={isLicenseViolationRoute} />}
          {expRoute &&
            !screenRoute &&
            !isLoginRoute &&
            !isErrorRoute &&
            !isFragmentRoute && (
              <ExperimentSidebar id={`layout-experimentsidebar`} to={splitPath[2]} />
            )}
          {isSearchAndFilterRoute && (
            <SNFSidebar id="layout-snfsidebar" to={splitPath[2]} />
          )}{' '}
          {/*todo: need to fix this, should be using withRouter  */}
          {!expRoute &&
            screenRoute &&
            !isLoginRoute &&
            !isErrorRoute &&
            !isFragmentRoute && (
              <ScreenSidebar id="layout-screensidebar" to={splitPath[2]} />
            )}
          {!expRoute &&
            !screenRoute &&
            !isLoginRoute &&
            !isErrorRoute &&
            !isSearchAndFilterRoute &&
            isFragmentRoute && <FragmentSidebar />}
        </div>
        <div
          id="layout-top-left"
          data-testid="layout-top-left"
          style={{
            display: 'flex',
            alignItems: 'center',
            gridArea: 'top-left',
            justifyContent: 'center',
            background: '#1B1B1B',
          }}
          onClick={() => {
            !isLicenseViolationRoute ? history.push('/') : window.location.replace('/');
          }}
        >
          <img
            id="layout-logo"
            data-testid="layout-logo"
            src={Logo}
            style={styles.logoImage}
          />
        </div>
        <div
          id="layout-center-container"
          data-testid="layout-center-container"
          style={{
            gridArea: 'center',
            height: '100%',
            position: 'absolute', // Added for focuslevel images height do not stretch from window
            width: '100%',
          }}
        >
          {props.children}
          {isProject && <AutoUpdate showFallback={false} />}
          <RMErrorModal
            hasError={uncaughtError.hasError}
            errorInfo={uncaughtError.errorMessage}
            stackTrace={uncaughtError.errorStack}
            errorTime={uncaughtError.errorTime}
            showClose={true}
            closeErrorModal={closeErrorModal}
            isLoadingStackTrace={uncaughtError.isLoadingStackTrace}
          />
          <RMModal
            id="layout-licensemodal"
            showModal={showLicensModal}
            setShowModal={(e) => {
              _setShowLicensModal(e);
            }}
            onOk={() => {
              _setShowLicensModal(false);
            }}
            body={'You need to update your license to use this feature.'}
            heading={'License Update'}
            okButtonText={'OK'}
            isOkOnly={true}
          />
        </div>
      </div>
    );
  }

  return props.children;
};

export default Layout;

const styles: IStyle = {
  logoImage: {
    cursor: 'pointer',
  },
};
